/**
 * EQuIP Utility Functions
 *
 * Shared utility functions for EQuIP data manipulation, formatting, and business logic.
 */

import type {
  ProviderPerformanceDetail,
  MeasureChartData,
} from './equip-types'
import { ROLES } from './roles'
import type { MeasureThresholdMap } from '@/hooks/equip/useMeasureThresholdMap'
import { getActiveMeasureNames } from './measureScheduleSelectors'

// =============================================================================
// Constants
// =============================================================================

/**
 * Fallback operational pass/fail threshold (89%), used ONLY when the config
 * table lookup misses (e.g., network failure, row not yet seeded). The
 * canonical source of truth is `och.equip_config_measure_schedule` surfaced
 * via `useMeasureThresholdMap`. Do not reference this in display logic —
 * use `measure.threshold` / `measure.thresholdDisplay` from the emitted
 * `MeasureChartData` instead.
 */
export const MEASURE_THRESHOLD = 89
/** Fallback display threshold (90%). See note on `MEASURE_THRESHOLD`. */
export const DISPLAY_THRESHOLD = 90

/** ED_SendOut is inverted (lower is better). Fallback threshold: <= 25% passes. */
const ED_SENDOUT_FALLBACK_THRESHOLD = 25
const ED_SENDOUT_FALLBACK_DISPLAY = 25

/**
 * Map frontend EQuIP roles to provider types
 * Used for role-based data scoping in leadership dashboards
 */
export const EQUIP_ROLES_TO_PROVIDER_TYPES: Record<string, string[]> = {
  [ROLES.EQUIP_APC_LEAD]: ['PCP', 'Psych Med', 'Palliative', 'Teletriage'],
  [ROLES.EQUIP_PHYSICIAN_LEAD]: ['PC Physician', 'Psychiatrist'],
  [ROLES.EQUIP_ADMIN]: [
    'PCP',
    'Psych Med',
    'Palliative',
    'Teletriage',
    'PC Physician',
    'Psychiatrist',
  ],
}

/**
 * Check if a user has a leadership role in EQuIP
 * Leaders see all measures that have data, regardless of specialty/quarter filters
 */
export function isEquipLeader(equipRole: string | null): boolean {
  if (!equipRole) return false
  return (
    equipRole === ROLES.EQUIP_APC_LEAD ||
    equipRole === ROLES.EQUIP_PHYSICIAN_LEAD ||
    equipRole === ROLES.EQUIP_ADMIN
  )
}

// =============================================================================
// Quarter Derivation
// =============================================================================

/**
 * Calendar-based quarter calculation (does NOT respect cutoff dates).
 *
 * @deprecated Use the useCurrentQuarter() hook for authoritative quarter
 * routing. This function is retained only as a fallback for non-React
 * contexts (scripts, utilities) where the hook cannot be used.
 */
export function getCurrentQuarter(): { year: number; quarter: number } {
  const now = new Date()
  const month = now.getMonth() + 1 // JavaScript months are 0-indexed
  const year = now.getFullYear()

  // January uses Q4 of prior year
  if (month === 1) {
    return { year: year - 1, quarter: 4 }
  }

  // Q1: Feb-Mar, Q2: Apr-Jun, Q3: Jul-Sep, Q4: Oct-Dec
  let quarter: number
  if (month >= 2 && month <= 3) {
    quarter = 1
  } else if (month >= 4 && month <= 6) {
    quarter = 2
  } else if (month >= 7 && month <= 9) {
    quarter = 3
  } else {
    quarter = 4
  }

  return { year, quarter }
}

// =============================================================================
// Measure Extraction and Aggregation
// =============================================================================

/** "as of 5/20/2026 1016 ET" — the cache rebuild time (Eastern) for non-PIMs measures. */
function formatRefreshedLabel(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return undefined
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d)
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? ''
  const hour = part('hour') === '24' ? '00' : part('hour') // some engines render midnight as 24
  return `as of ${part('month')}/${part('day')}/${part('year')} ${hour}${part('minute')} ET`
}

/** "from Apr 2026 audits" — the latest available audit month for PIMs. */
function formatPimsLabel(
  year: number | null | undefined,
  month: number | null | undefined
): string | undefined {
  if (!year || !month) return undefined
  const d = new Date(year, month - 1, 1)
  return `from ${d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} audits`
}

/**
 * Extract active measures from performance detail for chart rendering
 * Returns all applicable measures including zero-denominator entries
 * (measures with 0 eligible patients are marked with isZeroDenominator: true).
 * Final filtering by bonus_eligible_type × quarter is applied via
 * `getActiveMeasureNames` from `measureScheduleSelectors`.
 *
 * Thresholds are resolved from the config-driven `MeasureThresholdMap`. If a
 * lookup misses, fallback constants are used and a dev-mode warning is logged
 * so the gap is visible.
 *
 * @param detail - Provider performance detail
 * @param thresholdMap - Keyed lookup from `useMeasureThresholdMap(year, quarter)`
 * @param isLeader - True if viewing as a leader (leaders see all measures with data)
 * @returns Filtered array of measures based on specialty and quarter
 */
export function getActiveMeasures(
  detail: ProviderPerformanceDetail,
  thresholdMap: MeasureThresholdMap,
  isLeader: boolean = false
): MeasureChartData[] {
  const measures: MeasureChartData[] = []
  const role = detail.bonusEligibleType

  /**
   * Resolve operational + display thresholds for a measure, in 0-100 scale.
   * Falls back to the module-level constants if the config row is missing.
   * Pass custom fallback values for inverted/special measures (e.g., ED_SendOut).
   */
  const resolveThresholds = (
    measureName: string,
    fallback: { threshold: number; thresholdDisplay: number } = {
      threshold: MEASURE_THRESHOLD,
      thresholdDisplay: DISPLAY_THRESHOLD,
    }
  ): { threshold: number; thresholdDisplay: number } => {
    const pair = thresholdMap.get(measureName, role)
    if (!pair) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          `[equip] No threshold config for ${measureName}|${role} at ${detail.year} Q${detail.quarter}; using fallback ${fallback.threshold}/${fallback.thresholdDisplay}.`
        )
      }
      return fallback
    }
    // Config stores 0-1 decimals; emitted MeasureChartData uses 0-100.
    return {
      threshold: pair.threshold * 100,
      thresholdDisplay: pair.thresholdDisplay * 100,
    }
  }

  // Quality measures — thresholds resolved per-measure from config (supports
  // tiered quarterly thresholds like BCS Q2=45, Q3=65, Q4=90).
  const qualityMeasures = [
    { name: 'AWV', eligible: detail.awvEligibleCount, met: detail.awvMetCount, performance: detail.awvPerformance },
    { name: 'A1C', eligible: detail.a1cEligibleCount, met: detail.a1cMetCount, performance: detail.a1cPerformance },
    { name: 'BCS', eligible: detail.bcsEligibleCount, met: detail.bcsMetCount, performance: detail.bcsPerformance },
    { name: 'CRCS', eligible: detail.crcsEligibleCount, met: detail.crcsMetCount, performance: detail.crcsPerformance },
    { name: 'PHQ9', eligible: detail.phq9EligibleCount, met: detail.phq9MetCount, performance: detail.phq9Performance },
    { name: 'HTN', eligible: detail.htnEligibleCount, met: detail.htnMetCount, performance: detail.htnPerformance },
    { name: 'ACP', eligible: detail.acpEligibleCount, met: detail.acpMetCount, performance: detail.acpPerformance },
  ]

  qualityMeasures.forEach((m) => {
    const { threshold, thresholdDisplay } = resolveThresholds(m.name)
    if (m.eligible !== null && m.eligible > 0 && m.performance !== null) {
      // Backend returns 0-1 scale; convert to 0-100 for display
      const pct = m.performance * 100
      measures.push({
        measureName: m.name,
        measureType: 'quality',
        performance: pct,
        eligible: m.eligible,
        met: m.met ?? 0,
        passThreshold: pct >= threshold,
        threshold,
        thresholdDisplay,
      })
    } else {
      // Zero-denominator: measure is applicable but has no eligible patients
      measures.push({
        measureName: m.name,
        measureType: 'quality',
        performance: 0,
        eligible: 0,
        met: 0,
        passThreshold: true,
        threshold,
        thresholdDisplay,
        isZeroDenominator: true,
      })
    }
  })

  // ACO Visits (physician only). Config row handles the Q1=70/Q2+=90 tier.
  {
    const { threshold, thresholdDisplay } = resolveThresholds('ACO_Visits')
    if (
      detail.acoVisitsEligibleCount !== null &&
      detail.acoVisitsEligibleCount > 0 &&
      detail.acoVisitsPerformance !== null
    ) {
      const acoPct = detail.acoVisitsPerformance * 100
      measures.push({
        measureName: 'ACO_Visits',
        measureType: 'aco_visits',
        performance: acoPct,
        eligible: detail.acoVisitsEligibleCount,
        met: detail.acoVisitsMetCount ?? 0,
        passThreshold: acoPct >= threshold,
        threshold,
        thresholdDisplay,
      })
    } else {
      measures.push({
        measureName: 'ACO_Visits',
        measureType: 'aco_visits',
        performance: 0,
        eligible: 0,
        met: 0,
        passThreshold: true,
        threshold,
        thresholdDisplay,
        isZeroDenominator: true,
      })
    }
  }

  // ED SendOut (teletriage only, INVERTED — lower is better).
  {
    const { threshold, thresholdDisplay } = resolveThresholds('ED_SendOut', {
      threshold: ED_SENDOUT_FALLBACK_THRESHOLD,
      thresholdDisplay: ED_SENDOUT_FALLBACK_DISPLAY,
    })
    if (detail.edSendoutPerformance !== null) {
      const edPct = detail.edSendoutPerformance * 100
      measures.push({
        measureName: 'ED_SendOut',
        measureType: 'ed_sendout',
        performance: edPct,
        eligible: 0,
        met: 0,
        passThreshold: edPct <= threshold, // inverted
        threshold,
        thresholdDisplay,
      })
    } else {
      measures.push({
        measureName: 'ED_SendOut',
        measureType: 'ed_sendout',
        performance: 0,
        eligible: 0,
        met: 0,
        passThreshold: true,
        threshold,
        thresholdDisplay,
        isZeroDenominator: true,
      })
    }
  }

  // PIMs (consistency measure)
  {
    const { threshold, thresholdDisplay } = resolveThresholds('PIMs')
    if (detail.pimsPerformance !== null) {
      const pimsPct = detail.pimsPerformance * 100
      measures.push({
        measureName: 'PIMs',
        measureType: 'pims',
        performance: pimsPct,
        eligible: detail.pimsPatientCount ?? 0,
        met: 0,
        passThreshold: pimsPct >= threshold,
        threshold,
        thresholdDisplay,
      })
    } else {
      measures.push({
        measureName: 'PIMs',
        measureType: 'pims',
        performance: 0,
        eligible: 0,
        met: 0,
        passThreshold: true,
        threshold,
        thresholdDisplay,
        isZeroDenominator: true,
      })
    }
  }

  // Coding audits (consistency measure)
  {
    const { threshold, thresholdDisplay } = resolveThresholds('Coding')
    if (
      detail.codingAuditCount !== null &&
      detail.codingAuditCount > 0 &&
      detail.codingPerformance !== null
    ) {
      const codingPct = detail.codingPerformance * 100
      measures.push({
        measureName: 'Coding',
        measureType: 'coding',
        performance: codingPct,
        eligible: detail.codingAuditCount,
        // Bug B: passing-count comes from the cache (# audits >= threshold),
        // not a hardcoded 0. Falls back to 0 only when the backend omits it
        // (e.g. historical snapshots, which don't carry the count).
        met: detail.codingPassingCount ?? 0,
        passThreshold: codingPct >= threshold,
        threshold,
        thresholdDisplay,
      })
    } else {
      measures.push({
        measureName: 'Coding',
        measureType: 'coding',
        performance: 0,
        eligible: 0,
        met: 0,
        passThreshold: true,
        threshold,
        thresholdDisplay,
        isZeroDenominator: true,
      })
    }
  }

  // Quality audits (consistency measure — a.k.a. Signature Standards)
  {
    const { threshold, thresholdDisplay } = resolveThresholds('Quality_Audit')
    if (
      detail.qualityAuditCount !== null &&
      detail.qualityAuditCount > 0 &&
      detail.qualityAuditPerformance !== null
    ) {
      const qualAuditPct = detail.qualityAuditPerformance * 100
      measures.push({
        measureName: 'Quality_Audit',
        measureType: 'quality_audit',
        performance: qualAuditPct,
        eligible: detail.qualityAuditCount,
        // Bug B: passing-count from cache (# audits >= threshold), not hardcoded 0.
        met: detail.qualityAuditPassingCount ?? 0,
        passThreshold: qualAuditPct >= threshold,
        threshold,
        thresholdDisplay,
      })
    } else {
      measures.push({
        measureName: 'Quality_Audit',
        measureType: 'quality_audit',
        performance: 0,
        eligible: 0,
        met: 0,
        passThreshold: true,
        threshold,
        thresholdDisplay,
        isZeroDenominator: true,
      })
    }
  }

  // Stamp each measure with a data-freshness caption. Standard measures share
  // the cache rebuild time; PIMs reflects the latest available audit month.
  const refreshedLabel = formatRefreshedLabel(detail.dataRefreshedAt)
  const pimsLabel = formatPimsLabel(detail.pimsDataYear, detail.pimsDataMonth)
  for (const m of measures) {
    m.dataAsOfLabel = m.measureType === 'pims' ? pimsLabel : refreshedLabel
  }

  // Filter by the canonical `och.equip_config_measure_schedule` active-measure
  // set for this role × quarter. Leaders see everything; individual providers
  // see only measures marked active in config. When the schedule hasn't
  // loaded yet (empty items) we return measures unfiltered so the dashboard
  // doesn't flash an empty state during the initial fetch.
  if (isLeader) return measures
  const activeNames = getActiveMeasureNames(
    thresholdMap.items,
    detail.bonusEligibleType,
    detail.quarter
  )
  if (!activeNames) return measures
  return measures.filter((m) => activeNames.has(m.measureName))
}

/**
 * Determine measure type from measure name
 */
export function getMeasureType(
  measureName: string
): 'quality' | 'pims' | 'coding' | 'quality_audit' | 'ed_sendout' | 'aco_visits' {
  const upperName = measureName.toUpperCase()
  if (upperName === 'PIMS') return 'pims'
  if (upperName === 'CODING') return 'coding'
  if (upperName === 'QUALITY_AUDIT' || upperName === 'QUALITY AUDIT') return 'quality_audit'
  if (upperName === 'ED_SENDOUT') return 'ed_sendout'
  if (upperName === 'ACO_VISITS') return 'aco_visits'
  return 'quality'
}

/**
 * Get display name for a measure (for user-facing labels)
 */
export function getMeasureDisplayName(measureName: string): string {
  const displayNames: Record<string, string> = {
    'Quality_Audit': 'Signature Standards',
    'ACO_Visits': 'ACO Physician Visit',
    'ED_SendOut': 'ED SendOut',
  }
  return displayNames[measureName] || measureName
}

// =============================================================================
// Formatting Utilities
// =============================================================================

/**
 * Format percentage with 1 decimal place
 * Backend returns 0-1 scale; this converts to 0-100 display
 */
export function formatPercentage(value: number | null): string {
  if (value === null || value === undefined) return 'N/A'
  return `${(value * 100).toFixed(1)}%`
}

/**
 * Format currency with thousands separator and 2 decimal places
 */
export function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return 'N/A'
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * Get color for pass/fail status
 * Returns green for pass, red for fail
 */
export function getPassFailColor(passed: boolean): string {
  return passed ? 'var(--equip-pass)' : 'var(--equip-fail)'
}

/**
 * Get Badge variant for pass/fail status
 * Returns shadcn/ui Badge variant string for direct use with Badge component
 */
export function getPassFailBadgeVariant(
  passed: boolean
): 'lightSuccess' | 'lightError' {
  return passed ? 'lightSuccess' : 'lightError'
}
